import { DateTime } from "luxon";
import { useFieldArray } from "react-hook-form";
import classNames from "classnames";
import {
  Table,
  Select,
  DatePicker,
  MinusIcon,
  IconButton,
  SelectOption,
} from "@mwi/ui";

type ExceptionsProps = {
  watch: any;
  errors: any;
  control: any;
  setValue: any;
  getValues: any;
  loading: boolean;
  timeOptions: string[];
};

const Exceptions = ({
  watch,
  errors,
  loading,
  control,
  getValues,
  setValue,
  timeOptions,
}: ExceptionsProps): JSX.Element => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "exceptions",
  });

  const handleAddException = () => {
    append({
      is_open: "open",
      day: null,
      start_time: null,
      end_time: null,
    });
  };

  const handleTimeIntervals = (
    expIdx: number,
  ): { stOptions: string[]; etOptions: string[] } => {
    const startTime = DateTime.fromISO(
      watch(`exceptions.${expIdx}.start_time`),
    );
    const endTime = DateTime.fromISO(watch(`exceptions.${expIdx}.end_time`));

    const stOptions = timeOptions;
    let etOptions = timeOptions;

    // end time intervals should be after start time interval
    if (startTime.isValid) {
      etOptions = timeOptions.filter((ct) => ct > startTime.toFormat("T"));
    }

    // reset end time interval if start time interval matches or exceeds it
    if (startTime.isValid && endTime.isValid && startTime >= endTime) {
      setTimeout(() => {
        setValue(`exceptions.${expIdx}.end_time`, undefined);
      }, 0);
    }

    return { stOptions, etOptions };
  };

  const scheduleOptions = [
    { id: "open", name: "Open" },
    { id: "close", name: "Close" },
  ];

  return (
    <>
      <Table loading={loading}>
        <Table.Head>
          <Table.Row>
            <Table.Item as="th" className="w-[240px]">
              Day
            </Table.Item>
            <Table.Item as="th">Schedule</Table.Item>
            <Table.Item as="th" className="w-[185px]">
              From
            </Table.Item>
            <Table.Item as="th" className="w-[185px]">
              Until
            </Table.Item>
            <Table.Item as="th">Remove</Table.Item>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {fields && fields.length ? (
            <>
              {fields.map((exception, idx) => {
                const { stOptions, etOptions } = handleTimeIntervals(idx);

                return (
                  <Table.Row key={exception.id}>
                    <Table.Item className="relative py-2">
                      <DatePicker
                        size="small"
                        control={control}
                        defaultLabel="DD/MM/YY"
                        isRelativeToSelf={false}
                        name={`exceptions.${idx}.day`}
                        disabledDays={{ before: DateTime.now().toJSDate() }}
                        buttonClassName={classNames(
                          "!px-4 rounded",
                          "text-base",
                          "shadow-none !bg-grey-lightest !border-grey-lightest",
                          "!focus:border !focus:border-primary",
                          {
                            "!border-error-light":
                              errors?.exceptions?.[idx]?.day,
                          },
                        )}
                        panelClassName="!left-[6px] !top-[55px] !w-[320px] scale-90"
                      />
                    </Table.Item>
                    <Table.Item className="py-2">
                      <Select
                        size="small"
                        control={control}
                        placeholder="Select"
                        name={`exceptions.${idx}.is_open`}
                        buttonClassName={classNames(
                          "!px-4 !rounded",
                          "text-base",
                          "shadow-none !bg-grey-lightest !border-grey-lightest",
                          "!focus:border !focus:border-primary",
                          {
                            "!border-error-light":
                              errors?.exceptions?.[idx]?.is_open,
                          },
                        )}
                      >
                        {scheduleOptions.map((s, itemIdx) => (
                          <SelectOption key={itemIdx} value={s.id}>
                            {s.name}
                          </SelectOption>
                        ))}
                      </Select>
                    </Table.Item>
                    <Table.Item className="py-2">
                      <Select
                        size="small"
                        control={control}
                        placeholder="Select"
                        name={`exceptions.${idx}.start_time`}
                        buttonClassName={classNames(
                          "!px-4 !rounded",
                          "text-base",
                          "shadow-none !bg-grey-lightest !border-grey-lightest",
                          "!focus:border !focus:border-primary",
                          {
                            "!border-error-light":
                              errors?.exceptions?.[idx]?.start_time,
                          },
                        )}
                      >
                        {stOptions &&
                          stOptions.map((s, itemIdx) => (
                            <SelectOption key={itemIdx} value={s}>
                              {s}
                            </SelectOption>
                          ))}
                      </Select>

                      <div className="absolute inset-y-1/2 right-[-3px] w-[7px] h-[2px] bg-black"></div>
                    </Table.Item>
                    <Table.Item className="py-2">
                      <Select
                        size="small"
                        control={control}
                        placeholder="Select"
                        name={`exceptions.${idx}.end_time`}
                        buttonClassName={classNames(
                          "!px-4 !rounded",
                          "text-base",
                          "shadow-none !bg-grey-lightest !border-grey-lightest",
                          "!focus:border !focus:border-primary",
                          {
                            "!border-error-light":
                              errors?.exceptions?.[idx]?.end_time,
                          },
                        )}
                      >
                        {etOptions &&
                          etOptions.map((s, itemIdx) => (
                            <SelectOption key={itemIdx} value={s}>
                              {s}
                            </SelectOption>
                          ))}
                      </Select>
                    </Table.Item>
                    <Table.Item className="py-2">
                      <IconButton onClick={() => remove(idx)}>
                        <MinusIcon className="w-4 h-4" />
                      </IconButton>
                    </Table.Item>
                  </Table.Row>
                );
              })}
            </>
          ) : (
            <Table.Row className="relative">
              <Table.Item className="text-center" colSpan={5}>
                No exceptions set
              </Table.Item>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <p
        className="mt-6 font-semibold cursor-pointer text-primary w-max"
        onClick={handleAddException}
      >
        + Add exception
      </p>
    </>
  );
};

export default Exceptions;
